import React, { useEffect } from 'react';
import { Button, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import useMessage from 'src/app/hooks/useMessage';
import handleError from '@fuse/core/errorHandler';
import {
  useCreateInitiativeCategoriMutation,
  useUpdateInitiativeCategoriMutation,
} from 'src/app/services/initiativeCategoryApi';

const defaultValues = {
  nameUz: '',
  nameRu: '',
};
const ALLOWED_PATTERN = /^[\p{L}\p{N}]/u;

const InitiativeCategoriesForm = ({ tempCat, isEdit, onClose }) => {
  const { t } = useTranslation('translations');
  const [updateCategory, { isLoading: isUpdateLoading }] = useUpdateInitiativeCategoriMutation();
  const [createCategory, { isLoading: isCreateLoading }] = useCreateInitiativeCategoriMutation();
  const { showMessage } = useMessage();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues,
  });

  useEffect(() => {
    if (tempCat) {
      reset({
        nameUz: tempCat.nameUz || '',
        nameRu: tempCat.nameRu || '',
      });
    } else {
      reset(defaultValues);
    }
  }, [tempCat, reset]);

  const handleClose = () => {
    onClose();
    reset(defaultValues);
  };

  const makeRules = () => ({
    required: t('EmptyInput') || true,
    validate: (value) => {
      const v = (value ?? '').trim();
      if (!v) return t('EmptyInput') || 'This field cannot be empty';
      if (!ALLOWED_PATTERN.test(v)) {
        return (
          t('SpecialCharactersNotAllowed') ||
          "Only letters, numbers, spaces and - . , _ ( ) / & : + ' are allowed"
        );
      }
      return true;
    },
  });

  const onSubmit = async (data) => {
    const trimmedData = {
      nameUz: (data.nameUz ?? '').trim(),
      nameRu: (data.nameRu ?? '').trim(),
    };

    const message = isEdit ? t('SuccessfullyUpdated') : t('SuccessfullyAdded');

    const promise = isEdit
      ? updateCategory({ id: tempCat.id, ...trimmedData })
      : createCategory(trimmedData);

    promise
      .unwrap()
      .then(() => {
        showMessage({ message, variant: 'success' });
        handleClose();
      })
      .catch(handleError);
  };

  const nameUz = watch('nameUz') ?? '';
  const nameRu = watch('nameRu') ?? '';

  const handleTrimOnChange = (field) => (e) => {
    setValue(field, e.target.value.trimStart(), { shouldValidate: true, shouldDirty: true });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-16 pt-10 max-w-7xl">
      <Controller
        name="nameUz"
        control={control}
        rules={makeRules()}
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ''}
            label={t('Title') + ' (uzb.)'}
            error={!!errors.nameUz}
            helperText={errors.nameUz?.message}
            onChange={(e) => {
              field.onChange(e);
              handleTrimOnChange('nameUz')(e);
            }}
          />
        )}
      />

      <Controller
        name="nameRu"
        control={control}
        rules={makeRules()}
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ''}
            label={t('Title') + ' (рус.)'}
            error={!!errors.nameRu}
            helperText={errors.nameRu?.message}
            onChange={(e) => {
              field.onChange(e);
              handleTrimOnChange('nameRu')(e);
            }}
          />
        )}
      />

      <Button
        disabled={
          isUpdateLoading || isCreateLoading || !isValid || !nameUz.trim() || !nameRu.trim()
        }
        type="submit"
        className="w-full"
        variant="contained"
        color="secondary"
      >
        <span className="mx-8">{t('Save')}</span>
      </Button>
    </form>
  );
};

export default InitiativeCategoriesForm;
